
import { useEffect, useMemo } from "react"
import styled from "styled-components";
import { DateFormat, IDeliveryInformation, IPromo, OrderType, PromoCodeAction, ValidStatus, validOrderTime } from "@tiffin/core";
import { CheckoutSummary, Loading, TiffinTimePicker, TifynFooter, toast } from "@tiffin/components";
import { Constants, auth, cartHelper, cartMultiReducer, cloudFunction, userReducer } from "@tiffin/app-common";
import { useAppDispatch, useAppSelector } from "../hooks";
import { CheckoutForm } from "../components/checkout/CheckoutForm";
import TifynLogo from "../assets/images/tifyn-logo.png"
import { GuestDetailsModal } from "../components/checkout/GuestDetailsModal";
import { AccountModal } from "../components/checkout/AccountModal";

import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { IonButton, IonIcon, useIonToast } from "@ionic/react";
import { arrowBackOutline, cartOutline } from "ionicons/icons"
import { Alert, Button, Divider, Modal, message } from "antd";
import { useCallback, useRef, useState } from "react";
import moment, {Moment} from "moment";
import { loadStripe } from "@stripe/stripe-js"
import { Elements } from "@stripe/react-stripe-js"
import isEmpty from "lodash.isempty"
import { logInOutline, personAddOutline } from "ionicons/icons"

const CHECKOUT_EXPIRY_TIME = 300;

const stripePromise = loadStripe("pk_live_51JETNtB1JzwyqL7clQEb1WBH7x79wA9OFxRWX67Zrm8BIEhfszzcmbborwcaNACJiWSPPXtmMwsZAx8Mv6yg2oT900bWccQb0h")

export const Checkout = styled(BaseCheckout)`
  display: grid;
  grid-template-columns: 1fr auto;
  height: 100%;

  @media (max-width:768px) {
    grid-template-columns: 100%;
  }

  .header {
    background: #fff;
    padding: 20px;
    font-size: var(--font-size-medium);
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 5px;

    ion-icon {
      font-size: var(--font-size-xlarge);
    }

    .partner-name {
      flex-grow: 1;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .ordertype {
        background-color: var(--color-medium);
        display: flex;
        align-items: center;
        color: #fff;
        padding: 5px 10px;
        border-radius: 5px;
        width: fit-content;
        font-size: small;
      }
    }
  }

  .page {
    display: grid;
    grid-template-rows: auto auto 1fr auto;
    height: calc(var(--vh, 1vh) * 100);
    overflow: auto;

    .forms {
      display: flex;
      flex-direction: column;
      gap: 20px;

      .guest-details {
        padding: 20px;
        background: #fff;
        display: flex;
        flex-direction: column;
        gap: 10px;
        border-radius: 8px;
        box-shadow: rgba(128, 128, 128, 0.08) -2px 2px 2px, rgba(128, 128, 128, 0.04) 0px 2px 2px;

        @media (min-width:768px) {
          min-width: 425px;
        }

        .ant-divider {
          margin: 6px 0;
        }

        .heading {
          font-size: medium;
          font-weight: 600;
        }
  
        .label {
          font-weight: 600;
        }
        
        .contact-info-btn {
          margin-top: 10px;
        }

        .account-btns {
          display: grid;
          grid-template-columns: 1fr 1fr;

          .btn-container {
            display: flex;
            gap: 5px;
            align-items: center;

            ion-icon {
              font-size: medium;
            }
          }
        }

        .sign-in-message {
          padding: 0 10px;
          font-size: x-small;
        }
      }

      .checkout-form-container {
        background: #fff;
        border-radius: 8px;
        box-shadow: rgba(128, 128, 128, 0.08) -2px 2px 2px, rgba(128, 128, 128, 0.04) 0px 2px 2px;
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        & > div {
          padding: 0;
        }

        .section-title {
          font-weight: 600;
          font-size: medium;
          color: var(--color-dark);
        }

        .ant-divider {
          margin: 6px 0;
        }
      }
    }

    .checkout-container {
      margin: 20px;
      display: grid;
      grid-template-columns: 1fr 420px;
      gap: 20px;

      @media (max-width:768px) {
        display: flex;
        flex-direction: column;
        margin: 10px;
        gap: 20px;
      }
    }
  }
`

interface ICheckout {
  className?: string
}

interface IGuestDetails {
  firstName: string,
  lastName: string,
  email: string,
  phonePrefix: string,
  phone: string
}

function BaseCheckout({className}: ICheckout) {

  const partner = useAppSelector((state) => state.store)
  const cart = useAppSelector((state) => partner.details?.id && state.cartMulti.carts[partner.details.id] ? state.cartMulti.carts[partner.details.id] : cartMultiReducer.EmptyCart);
  const cartStatus = useAppSelector((state) => state.cartMulti.status)
  const customerId = useAppSelector((state) => state.user.customerId)
  const authenticateStatus = useAppSelector((state) => state.authenticate.status)
  const dispatch = useAppDispatch()

  //Internal states
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState<boolean>(false)
  const [clientSecret, setClientSecret] = useState<string>()
  const [amount, setAmount] = useState<number>(0)
  const [orderId, setOrderId] = useState<string>()
  const [showTimePickerModal, setShowTimePickerModal] = useState(false)
  const [checkoutExpireSec, setCheckoutExpireSec] = useState<number | undefined>()
  const [showExpiredAlert, setShowExpiredAlert] = useState(false)
  const [appliedPromo, setAppliedPromo] = useState<IPromo>()
  const [specialInstructions, setSpecialInstructions] = useState<string>()
  const [deliveryFee, setDeliveryFee] = useState<number>()
  const [deliveryInformation, setDeliveryInformation] = useState<IDeliveryInformation>()
  const [paymentFee, setPaymentFee] = useState<number>()
  const [showGuestDetails, setShowGuestDetails] = useState<boolean>(false)
  const [guestDetails, setGuestDetails] = useState<IGuestDetails>()
  const [showSignInModal, setShowSignInModal] = useState<boolean>(false)
  const guestDetailsRef = useRef<IGuestDetails>()

  const isLoaded = useAppSelector((state) => !!state.store.details)
  let { date } = useParams();

  const initiatedCheckout = useRef(false);

  const expireTimeRef = useRef(moment().add(CHECKOUT_EXPIRY_TIME, 'seconds'))
  const expiringTimerRef = useRef<NodeJS.Timeout>();
  const navigate = useNavigate()
  const [searchParams] = useSearchParams();

  const pageRef = useRef<any>();

  const [messageApi] = message.useMessage();
  const [present] = useIonToast();

  //Validate time and only return value if valid
  const time = useMemo(() => {
    if(partner.details && date && cart.times?.[date]) {
      let orderTime = cart.times?.[date] === "now" ? moment() : moment(cart.times?.[date]);

      const adjustedDate = cart.orderType === OrderType.Pickup || cart.orderType === OrderType.DineIn ?
      partner.details.adjustedDates?.[orderTime.format(DateFormat)]?.pickupTimings :
      partner.details.adjustedDates?.[orderTime.format(DateFormat)]?.deliveryTimings

      const orderDayTimings = cart.orderType === OrderType.Pickup || cart.orderType === OrderType.DineIn ? partner.details.pickupDays?.[orderTime.day()] : partner.details.deliveryDays?.[orderTime.day()]

      if(!orderDayTimings) return

      const validStatus = validOrderTime(
        moment(cart.times?.[date]), 
        cart.times?.[date] === "now", 
        moment.tz.guess(), 
        orderDayTimings, 
        partner.details.preparationEnabled,
        partner.details.preparationDurationType,
        partner.details.preparationDurationType,
        adjustedDate,
        partner.details.preparationInMins
      )

      if(validStatus === ValidStatus.Valid) {
        return cart.times?.[date]
      } else if(validStatus === ValidStatus.StoreClosed) {
        toast(present, Constants.ERRORMESSAGE_STORE_CLOSED, "danger")
      } else if(validStatus === ValidStatus.InsufficientPrep, "danger") {
        toast(present, Constants.ERRORMESSAGE_STORE_NOT_ACCEPTING_ORDERS, "danger")
      }
    }
    return;
  }, [date, partner.details, cart.orderType, cart.times])

  useEffect(() => {
    if(cartStatus !== "idle") return

    if(!date) {
      toast(present, Constants.ERRORMESSAGE_UNEXPECTED_ERROR, "danger")
      return;
    }

    if (cart.orderType === OrderType.Delivery) {
      if (!cart.deliveryAddress) {
        toast(present, "No delivery address specified", "danger")
        return
      }
    }

    if (isEmpty(cart.items)) {
      toast(present, "Cart is empty", "danger")
      return
    }

    if(!cart.times?.[date]) {
      setShowTimePickerModal(true);
    }
  }, [date, cart, cartStatus, present])

  useEffect(() => {
    if(!customerId) {
      //Fetch the user again, user maybe newly created so customerId may not be populated
      dispatch(userReducer.fetchUser())
    }
  }, [customerId, dispatch])

  const expiryTimerUpdate = useCallback(async () => {
    expiringTimerRef.current = setInterval(() => {
      setCheckoutExpireSec((val) => {
        const secondsLeft = (expireTimeRef.current.valueOf() - moment().valueOf()) / 1000;
        if(secondsLeft <= 0) {
          setClientSecret(undefined)
          setAppliedPromo(undefined)
          setSpecialInstructions(undefined)
          setDeliveryFee(undefined)
          setPaymentFee(undefined)

          setShowExpiredAlert(true)
          if(expiringTimerRef.current) {
            clearInterval(expiringTimerRef.current);
          }
          return 0;
        }
        return secondsLeft
      });
    }, 1000);
  }, [])


  const removePromo = useCallback(async () => {
    setLoading(true)
    setAppliedPromo(undefined)
    const promise = cloudFunction("updatePromoCode", {
      orderId: orderId,
      action: PromoCodeAction.Remove
    })

    promise.then((val: any) => {
      if(!val.data.clientSecret && !val.data.amount) {
        messageApi.error("An error occurred removing the promotion code, please contact Tifyn support", 3000);
        return;
      }

      setClientSecret(val.data.clientSecret)
      setAmount(val.data.amount)
      setPaymentFee(val.data.paymentFee)
      setLoading(false)
    }, (err) => {
      messageApi.error("An unexpected error occurred, if the error persists please contact Tifyn support", 3000);
      setLoading(false)
    })

    return promise
  }, [orderId, messageApi])

  const applyPromo = useCallback(async (promoCode: string) => {
    setLoading(true)

    const promise = cloudFunction("updatePromoCode", {
      orderId: orderId,
      action: PromoCodeAction.Apply,
      promoCode: promoCode
    });

    promise.then((val: any) => {
      if(!val.data.clientSecret && !val.data.amount) {
        messageApi.error("An error occurred applying the promotion code, please contact Tifyn support", 3000);
        return;
      }

      setClientSecret(val.data.clientSecret)
      setAmount(val.data.amount)
      setPaymentFee(val.data.paymentFee)
      setAppliedPromo(val.data.appliedPromo)
      setLoading(false)
    }, (err) => {
      setLoading(false)
    })

    return promise
  }, [orderId, messageApi])

  const initiateCheckout = useCallback(async () => {
    setLoading(true)

    if(!partner.details?.id) return;

    let request: any = {
      partnerId: partner.details?.id,
      date: date,
      timezone: moment.tz.guess(),
      isPartnerWebsite: true,
      orderId: orderId
    }

    const constructCart = (request: any) => {
      const checkoutCart: any = {
        partnerId: cart.partnerId,
        partnerName: cart.partnerName,
        orderType: cart.orderType,
        times: cart.times
      };

      if(cart.tableNumber) {
        checkoutCart.tableNumber = cart.tableNumber
      }

      if(cart.deliveryAddress) {
        checkoutCart.deliveryAddress = cart.deliveryAddress
      }

      if(date && cart.items?.[date]) {
        checkoutCart.items = {}
        checkoutCart.items[date] = cart.items?.[date].map((val) => ({
          id: val.details.id,
          count: val.count,
          modifiers: val.modifiers
        }))
      }

      if(date && cart.orderId) {
        checkoutCart.orderId = cart.orderId
        setOrderId(checkoutCart.orderId)
      }

      request.cart = checkoutCart;

      return request;
    }

    if(!auth.currentUser && !guestDetailsRef.current) {
      setShowGuestDetails(true)
      setLoading(false)
      return;
    }

    if(cart.orderType === OrderType.DineIn) {
      request = constructCart(request)
    } else {
      //Pickup or delivery order
      if(!auth.currentUser) {
        request = constructCart(request)
      }
    }

    if(!auth.currentUser && guestDetailsRef.current) {
      request.user = {
        firstName: guestDetailsRef.current.firstName,
        lastName: guestDetailsRef.current.lastName,
        phonePrefix: `+${guestDetailsRef.current.phonePrefix}`,
        phone: guestDetailsRef.current.phone,
        email: guestDetailsRef.current.email
      }
    }

    cloudFunction("checkout", request).then((result: any) => {
      if(result.data.appliedPromo) {
        setAppliedPromo(result.data.appliedPromo)
      }

      if(result.data.specialInstructions) {
        setSpecialInstructions(result.data.specialInstructions)
      }

      if(result.data.deliveryFee !== undefined) {
        setDeliveryFee(result.data.deliveryFee)
      }

      if(result.data.deliveryInformation) {
        setDeliveryInformation(result.data.deliveryInformation)
      }

      if(result.data.paymentFee) {
        setPaymentFee(result.data.paymentFee)
      }

      //Start expiry timer
      expireTimeRef.current = moment().add(CHECKOUT_EXPIRY_TIME, 'seconds')
      expiryTimerUpdate()

      setLoading(false)
      if(result.data.error) {
        setError(result.data.error)
      } else {
        setOrderId(result.data.orderId)
        if(!auth.currentUser && partner.details?.id) {
          //Store order id in session
          cartHelper.storeGuestOrderId(partner.details?.id, result.data.orderId)
        }
        setClientSecret(result.data.clientSecret)
        setAmount(result.data.amount)
        setError(undefined)
      }
    }, (error) => {
      initiatedCheckout.current = false;
      if(error.details?.message) {
        messageApi.error(error.details?.message);
        setError(error.details.message)
      } else if(error.message) {
        messageApi.error(error.message);
        setError(error.message)
      }
      setLoading(false)
    })
  }, [partner.details?.id, guestDetailsRef, date, messageApi, expiryTimerUpdate, orderId, cart.deliveryAddress, cart.times, cart.orderId, cart.items, cart.orderType, cart.partnerId, cart.partnerName, cart.tableNumber])

  useEffect(() => {
    if(!initiatedCheckout.current && time) {
      initiatedCheckout.current = true;
      initiateCheckout()
    }
  }, [time, initiateCheckout])

  const handleDeliveryChange = (amount: number | null, deliveryFee: number, deliveryInformation: IDeliveryInformation) => {
    if(amount !== null) {
      setAmount(amount)
    }
    setDeliveryFee(deliveryFee)
    setDeliveryInformation(deliveryInformation)
  }

  const updateGuestDetails = (values: any) => {
    setShowGuestDetails(false)
    guestDetailsRef.current = values
    setGuestDetails(values)

    if(orderId) {
      setLoading(true)
      cloudFunction("updateGuestDetails", {
        orderId: orderId,
        firstName: values.firstName,
        lastName: values.lastName,
        phonePrefix: values.phonePrefix,
        phone: values.phone,
        email: values.email
      }).then((result: any) => {
        setLoading(false)
        if(result.data.status !== "success") {
          setError("An unexpected error occurred when updating contact details")
        }
      })
    } else {
      initiateCheckout()
    }
  }

  function changeGuestDetails() {
    setShowGuestDetails(true)
  }

  const options = {
    // passing the client secret obtained from the server
    clientSecret: clientSecret,
  };

  const orderTypeLabel = () => {
    if(cart.orderType === OrderType.Pickup) {
      return 'Pickup Order'
    } else if(cart.orderType === OrderType.Delivery) {
      return 'Delivery Order'
    } else if(cart.orderType === OrderType.DineIn) {
      return 'Dine In Order'
    }
  }

  return (
    <div className={className}>
      <Loading isOpen={loading} showBox={true} message="Processing" />
      <div className="page">
        {
          isLoaded && date &&
          <>
            <div className="header">
              <Button type="link" onClick={() => navigate(-1)}><IonIcon icon={arrowBackOutline} /></Button>
              <IonIcon icon={cartOutline} />
              <div className="partner-name">
                <span>{partner.details?.name}</span>
                <div className="ordertype">{orderTypeLabel()}</div>
              </div>
            </div>
            <div className="checkout-alerts">
              {
                !cart.times?.[date] &&
                <Alert showIcon message={`Please select a ${cart.orderType === OrderType.Pickup ? 'pickup' : 'delivery'} time`} type="error" />
              }
              {
                error &&
                <Alert showIcon message={error} type="error" />
              }
              {
                checkoutExpireSec !== undefined && checkoutExpireSec > 0 && checkoutExpireSec <= 120 &&
                <Alert showIcon message={`Your session will expire in ${checkoutExpireSec > 60 ? `${Math.floor(checkoutExpireSec / 60)} ${Math.floor(checkoutExpireSec / 60) === 1 ? 'minute' : 'minutes'} and` : ''} ${Math.floor(checkoutExpireSec % 60)} seconds`} />
              }
              {
                checkoutExpireSec === 0 &&
                <Alert showIcon type="warning" message={`Your session has expired`} />
              }
            </div>
            <div className="checkout-container">
              <div className="checkout-summary-container">
                {
                  cart.items?.[date] && partner.details &&
                  <CheckoutSummary
                    orderId={orderId} 
                    date={date} 
                    orderTime={time}
                    items={cart.items![date]} 
                    orderType={cart.orderType}
                    tiffin={partner.details} 
                    deliveryAddress={cart.deliveryAddress}
                    pageRef={pageRef}
                    applyPromoCallback={applyPromo}
                    removePromoCallback={removePromo}
                    appliedPromo={appliedPromo}
                    specialInstructions={specialInstructions}
                    cart={cart}
                    cartStatus={cartStatus}
                    isPartnerWebsite={true}
                    deliveryFee={deliveryFee}
                    deliveryInformation={deliveryInformation}
                    deliveryAddressUpdateCallback={handleDeliveryChange}
                    paymentFee={paymentFee}
                  />
                }
              </div>
              <div className="forms">
              {
                (authenticateStatus !== "authenticated" && guestDetails) &&
                <div className="guest-details">
                  <div className="heading">Contact Information</div>
                  <Divider />
                  <div>
                    <span className="label">Name: </span>
                    <span>{guestDetails?.firstName} {guestDetails?.lastName}</span>
                  </div>
                  <div>
                    <span className="label">Email: </span>
                    <span>{guestDetails?.email}</span>
                  </div>
                  <div>
                    <span className="label">Phone: </span>
                    <span>{guestDetails?.phone}</span>
                  </div>
                  <IonButton color="light" size="small" className="contact-info-btn " onClick={() => changeGuestDetails()}>Change</IonButton>
                  <div className="account-btns">
                    <IonButton color="primary" size="small" onClick={() => setShowSignInModal(true)}><div className="btn-container"><IonIcon icon={logInOutline}/>Sign In</div></IonButton>
                    <IonButton color="primary" size="small" onClick={() => setShowSignInModal(true)}><div className="btn-container"><IonIcon icon={personAddOutline}/>Create Account</div></IonButton>
                  </div>
                  <span className="sign-in-message">Sign in or create an account for a faster checkout experience</span>
                </div>
              }
              {
                clientSecret && 
                <div className="checkout-form-container">
                  <div className="section-title">Payment method</div>
                  <Divider />
                  <Elements key={amount} stripe={stripePromise as any} options={options}>
                    <CheckoutForm 
                      date={date}
                      orderId={orderId}
                      amount={amount}
                      loading={loading}
                      clientSecret={clientSecret}
                      tableNumber={cart.tableNumber}
                    /> 
                  </Elements>
                </div>
              }
              </div>
              {
                partner.details &&
                <TiffinTimePicker
                  isOpen={showTimePickerModal}
                  onClose={() => {
                    setShowTimePickerModal(false)
                  }}
                  onChange={(time: Moment) => {
                    if(partner.details?.id && date) {
                      dispatch(cartMultiReducer.updateTime({id: partner.details?.id, date: date, time: time.format()}))
                      setShowTimePickerModal(false);
                    }
                  }}
                  orderDate={date}
                  tiffin={partner.details}
                  orderType={cart.orderType}
                  title={`Select a ${cart.orderType === OrderType.Pickup ? 'pickup' : 'delivery'} time`}
                />
              }
            </div>
            <Modal
              open={showExpiredAlert}
              title='Your session has expired'
              footer={[
                <Button onClick={() => {
                  navigate({pathname: `/${partner.details?.urlName}/cart`, search: searchParams.toString()})
                  setShowExpiredAlert(false)
                }}>Cancel</Button>,
                <Button type="primary" onClick={() => {
                    setShowExpiredAlert(false)
                    initiateCheckout() 
                    expireTimeRef.current = moment().add(CHECKOUT_EXPIRY_TIME, 'seconds');
                }}>Renew</Button>
              ]}
            >
            </Modal>
            <GuestDetailsModal 
              isOpen={showGuestDetails} 
              onCancel={() => {
                if(!guestDetailsRef.current) {
                  navigate({pathname: `/${partner.details?.urlName}/cart`, search: searchParams.toString()})
                }
                setShowGuestDetails(false)
              }} 
              handleUpdate={updateGuestDetails} 
            />
            <AccountModal 
              isOpen={showSignInModal}
              onClose={() => setShowSignInModal(false)}
              onSuccess={() => initiateCheckout()}
              userDetails={guestDetails}
            />
          </>
        }
        <TifynFooter
          logoAlt="Tifyn Logo"
          logoSrc={TifynLogo}
        />
      </div>
    </div>
  )
}